const sliceFromLocaleMap = (o, locale) => {
  if (locale === "en") {
    return typeof o.en === "string" ? o.en : "";
  }
  if (typeof o.fr === "string" && o.fr.trim()) return o.fr;
  return typeof o.en === "string" ? o.en : "";
};

/**
 * Resolves a localized Payload field to a single string for the active locale.
 * Handles plain strings, `{ en, fr }` objects, and JSON-stringified locale maps.
 *
 * @param {unknown} raw
 * @param {'en' | 'fr'} [locale]
 * @returns {string}
 */
export function localizedStringForLocale(raw, locale = "en") {
  if (raw == null || raw === "") return "";

  if (typeof raw === "object" && !Array.isArray(raw)) {
    return sliceFromLocaleMap(raw, locale);
  }

  if (typeof raw === "string") {
    const s = raw.trim();
    if (s.startsWith("{")) {
      try {
        return sliceFromLocaleMap(JSON.parse(s), locale);
      } catch {
        return raw;
      }
    }
    return raw;
  }

  return "";
}

/**
 * @typedef {Object} EntertainmentCard
 * @property {string|number} [id] - Payload document id (stable identity for list/preview)
 * @property {string} name
 * @property {string} title
 * @property {string} text
 * @property {string} image
 * @property {string} [imageAlt]
 * @property {string} [event_info]
 * @property {string} [event_url]
 * @property {string} [ticket_url]
 * @property {string} [category] - Payload slug: live-music | variety
 * @property {string} [special_note]
 * @property {string} [publish_at]
 * @property {string} [unpublish_at]
 */

/**
 * Maps a Payload `entertainment-events` document to the legacy ERS card shape
 * consumed by Entertainment.jsx and shouldPublish().
 *
 * @param {Record<string, unknown>} doc - Payload REST document
 * @param {'en' | 'fr'} [locale]
 * @returns {EntertainmentCard}
 */
export function mapEntertainmentEvent(doc, locale = "en") {
  const imageMedia =
    doc.imageMedia && typeof doc.imageMedia === "object" ? doc.imageMedia : null;

  const eventUrl = localizedStringForLocale(doc.eventUrl, locale);
  const ticketUrl = localizedStringForLocale(doc.ticketUrl, locale);

  return {
    id: doc.id != null ? doc.id : undefined,
    name: doc.name,
    title: doc.title,
    text: doc.text,
    image: doc.image,
    imageAlt: imageMedia?.alt || doc.name,
    event_info: doc.specialNotes || doc.subtitle || "",
    event_url: eventUrl || undefined,
    ticket_url: ticketUrl || undefined,
    category: doc.category,
    special_note: doc.subtitle || "",
    publish_at: doc.publishAt || undefined,
    unpublish_at: doc.unpublishAt || undefined,
  };
}

/**
 * @param {{ docs?: unknown[] }} payloadResponse
 * @param {'en' | 'fr'} [locale]
 * @returns {EntertainmentCard[]}
 */
export function mapEntertainmentResponse(payloadResponse, locale = "en") {
  const docs = Array.isArray(payloadResponse?.docs) ? payloadResponse.docs : [];
  return docs.map((doc) => mapEntertainmentEvent(doc, locale));
}
